import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import { Chip, Empty } from '../kit.js';
import { cameraById } from '../../data/cameras.js';
import { CountdownRing, Icon, LockGlyph } from './glyphs.js';
import { CameraFeed, Countdown, Elapsed, EventRow, useNow } from './parts.js';
import { STATES, formatHour, hoursOf, scheduleFor, timeOf } from './model.js';

const EASE = [0.16, 1, 0.3, 1];

/* States in which the door is not holding: the shackle is drawn open. */
export const isOpenState = state => state === 'unlocked' || state === 'open' || state === 'held' || state === 'forced';

/* The padlock in its coloured disc, with the countdown ring round it
   while a momentary unlock runs. */
export function LockBadge({ state, pulseUntil = 0, size = 'md' }) {
    const remaining = Math.max(0, pulseUntil - Date.now());
    const px = size === 'lg' ? 76 : 52;

    return (
        <span className={`acc-lock acc-lock--${size}`} data-state={state} data-tone={STATES[state].tone}>
            {state === 'open' && remaining > 0 && (
                <CountdownRing key={pulseUntil} duration={remaining / 1000} size={px} stroke={size === 'lg' ? 3.5 : 2.5} />
            )}
            <LockGlyph open={isOpenState(state)} size={size === 'lg' ? 34 : 22} />
            {state === 'offline' && <span className="acc-lock__slash" aria-hidden="true" />}
        </span>
    );
}

/* The chip a door card leads with. Held and forced doors count up from
   when they went into alarm; a momentary unlock counts down. */
export function StateChip({ door, state }) {
    const meta = STATES[state];
    return (
        <Chip tone={meta.tone}>
            {meta.label}
            {(state === 'held' || state === 'forced') && door.since ? <> <Elapsed since={door.since} /></> : null}
            {state === 'open' ? <> <Countdown until={door.pulseUntil} /></> : null}
        </Chip>
    );
}

/* The buttons for a door, shared by its card and its detail sheet.
   What is offered follows what the door is doing: an alarm wants
   acknowledging, an offline reader reconnecting, the rest unlocking.
   The card (`compact`) shows the two that matter; the sheet shows all. */
export function DoorActions({ door, state, freePassage, lockdown, reconnecting, act, compact }) {
    const sm = compact ? ' btn-sm' : '';
    /* Twelve solid accent buttons turned the grid into a wall of
       buttons, so the card's main action is a tinted one; the sheet,
       which has one door, keeps the solid fill. */
    const main = compact ? 'btn acc-btn-soft' : 'btn btn-primary';

    if (lockdown) {
        return <span className="acc-door__note"><Icon name="shield" size={15} /> Held locked by lockdown</span>;
    }
    if (state === 'offline') {
        return (
            <button type="button" className={`btn btn-outline-light${sm}`} onClick={() => act('reconnect', door)} disabled={reconnecting}>
                <Icon name="refresh" size={15} className={reconnecting ? 'acc-spin' : ''} />
                {reconnecting ? 'Reconnecting' : 'Reconnect reader'}
            </button>
        );
    }
    if (state === 'held' || state === 'forced') {
        return (
            <button type="button" className={`btn acc-btn-alarm${sm}`} onClick={() => act('ack', door)}>
                <Icon name="check" size={15} /> Acknowledge alarm
            </button>
        );
    }

    const unlocked = state === 'unlocked';
    const resume = door.override && (
        <button type="button" className={`btn btn-outline-light${sm}`} onClick={() => act('resume', door)}>
            {!compact && <Icon name="clock" size={15} />} {freePassage ? 'Back to free passage' : 'Resume schedule'}
        </button>
    );

    if (unlocked) {
        return (
            <>
                <button type="button" className={`${main}${sm}`} onClick={() => act('toggle', door)} aria-pressed="true">
                    <Icon name="shield" size={15} /> Lock now
                </button>
                {resume}
            </>
        );
    }

    return (
        <>
            <button type="button" className={`${main}${sm}`} onClick={() => act('pulse', door)}>
                <Icon name="door" size={15} /> {state === 'open' ? 'Extend 5s' : 'Unlock 5s'}
            </button>
            <button type="button" className={`btn btn-outline-light${sm}`} onClick={() => act('toggle', door)} aria-pressed="false">
                {!compact && <Icon name="key" size={15} />} Keep unlocked
            </button>
            {!compact && resume}
        </>
    );
}

/* One door in the staff grid. The lock is the card's headline; the
   chip says the same in words, and counts while the state is timed.
   It forwards its ref because the grid's AnimatePresence pops a card
   out of the layout as it leaves, and needs the element to measure. */
export const DoorCard = React.memo(React.forwardRef(function DoorCard({ door, state, hours, freePassage, lockdown, reconnecting, act }, ref) {
    const reduced = useReducedMotion();
    const last = door.last;

    return (
        <motion.article
            ref={ref}
            layout={!reduced}
            className="acc-door"
            data-state={state}
            data-tone={STATES[state].tone}
            initial={reduced ? false : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45, ease: EASE, layout: { duration: 0.5, ease: EASE } }}
        >
            <span className="acc-door__wash" aria-hidden="true" />
            <div className="acc-door__top">
                <button type="button" className="acc-door__badge" onClick={() => act('open', door)} aria-label={`${door.name}: details and camera`}>
                    <LockBadge state={state} pulseUntil={door.pulseUntil} />
                </button>
                <div className="acc-door__name">
                    <h4>{door.name}</h4>
                    <span className="acc-door__sub">
                        <StateChip door={door} state={state} />
                        <span className="acc-door__zone">{door.zone}</span>
                    </span>
                </div>
                <button type="button" className="acc-door__more" onClick={() => act('open', door)} aria-label={`${door.name}: camera and details`}>
                    <Icon name="camera" size={16} />
                </button>
            </div>

            <dl className="acc-door__facts">
                <div>
                    <dt>Last</dt>
                    <dd className="acc-door__last">
                        <span title={last ? `${last.title}, ${last.who || ''}` : undefined}>
                            {last ? <><span className={`acc-door__dot acc-door__dot--${last.tone}`} />{last.label}{last.who ? ` · ${last.who}` : ''}</> : 'No activity today'}
                        </span>
                        {last && <time className="acc-num">{timeOf(last.ts)}</time>}
                    </dd>
                </div>
                <div>
                    <dt>Hours</dt>
                    <dd>{door.override ? <span className="acc-override">{door.override === 'unlocked' ? 'Kept unlocked by front desk' : 'Kept locked by front desk'}</span> : hours}</dd>
                </div>
                <div>
                    <dt>Reader</dt>
                    <dd>{door.reader}</dd>
                </div>
            </dl>

            <div className="acc-door__actions">
                <DoorActions door={door} state={state} freePassage={freePassage} lockdown={lockdown} reconnecting={reconnecting} act={act} compact />
            </div>
        </motion.article>
    );
}));

/* A 24-hour bar with the door's open window lit and a marker at now. */
function DayTrack({ schedule, holiday }) {
    const now = useNow(60000);
    const d = new Date(now);
    const at = ((d.getHours() + d.getMinutes() / 60) / 24) * 100;
    const [open, close] = schedule && schedule.enabled ? hoursOf(schedule, holiday) : schedule ? [0, 0] : [0, 24];

    return (
        <div className="acc-track" aria-hidden="true">
            <div className="acc-track__bar">
                <motion.span
                    className="acc-track__fill"
                    initial={false}
                    animate={{ left: `${(open / 24) * 100}%`, width: `${((close - open) / 24) * 100}%` }}
                    transition={{ duration: 0.6, ease: EASE }}
                />
                <span className="acc-track__now" style={{ left: `${at}%` }} />
            </div>
            <div className="acc-track__ticks">
                <span>12 AM</span><span>6 AM</span><span>12 PM</span><span>6 PM</span><span>12 AM</span>
            </div>
        </div>
    );
}

/* The detail sheet for one door: its camera, its schedule, its recent
   history. Rendered inside a Modal by the staff screen. */
export function DoorDetail({ door, state, events, schedules, holiday, hours }) {
    const camera = cameraById(door.camera);
    const schedule = scheduleFor(door, schedules);
    const mine = events.filter(e => e.door === door.id).slice(0, 6);

    return (
        <>
            <div className="acc-detail">
                {camera && (
                    <CameraFeed camera={camera}>
                        <div className="acc-cam__state">
                            <LockBadge state={state} pulseUntil={door.pulseUntil} />
                            <div>
                                <StateChip door={door} state={state} />
                                <span className="acc-cam__door">{door.name}</span>
                            </div>
                        </div>
                    </CameraFeed>
                )}

                <dl className="acc-specs">
                    <div><dt>Reader</dt><dd>{door.reader}</dd></div>
                    <div><dt>Hardware</dt><dd>{door.hardware}</dd></div>
                    <div><dt>Location</dt><dd>{door.zone}</dd></div>
                    <div><dt>Access group</dt><dd>{door.group}</dd></div>
                </dl>
            </div>

            <section className="acc-sheet">
                <div className="acc-sheet__head">
                    <h4>Schedule</h4>
                    {schedule ? <Chip tone={schedule.enabled ? 'ok' : 'muted'}>{schedule.enabled ? 'On' : 'Off'}</Chip> : <Chip tone="info">24 hours</Chip>}
                </div>
                <p className="acc-sheet__line">{door.override ? `${door.override === 'unlocked' ? 'Kept unlocked' : 'Kept locked'} by the front desk. Its schedule: ${hours}` : hours}</p>
                <DayTrack schedule={schedule} holiday={holiday} />
                {schedule && (
                    <dl className="acc-specs acc-specs--3">
                        <div><dt>Regular</dt><dd>{formatHour(schedule.open)} to {formatHour(schedule.close)}</dd></div>
                        <div><dt>Holiday</dt><dd>{formatHour(schedule.holiday[0])} to {formatHour(schedule.holiday[1])}</dd></div>
                        <div><dt>Applies to</dt><dd>{schedule.who}</dd></div>
                    </dl>
                )}
            </section>

            <section className="acc-sheet">
                <div className="acc-sheet__head">
                    <h4>Recent events</h4>
                    <span className="acc-faint">{mine.length ? `Last ${mine.length}` : ''}</span>
                </div>
                {mine.length === 0 ? <Empty>Nothing at this door yet today.</Empty> : (
                    <ul className="acc-list">
                        {mine.map(e => <li key={e.id}><EventRow event={e} door={false} /></li>)}
                    </ul>
                )}
            </section>
        </>
    );
}
